/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {startTransition, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {Link as RouteLink} from 'react-router-dom'

// Components
import {
    Box,
    Button,

    // Hooks
    useTheme
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import {Link as ChakraLink} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ChevronDownIcon} from '../icons'

// Others
import {categoryUrlBuilder} from '../../utils/url'
import useNavigationHandler, {getNavigationProps} from '../../hooks/use-navigation-handler'
import useTagManager from '../../hooks/use-tag-manager'
import useMonetate from '../../hooks/use-monetate'
import LoadingSpinner from '../loading-spinner'
import {scheduleAfterPaint, scheduleIdleAnalytics} from '../../utils/common-utils'

const MENU_HOVER_INTENT_DELAY_MS = 150

const ListMenuTrigger = ({item, name, isOpen, onOpen, onClose}) => {
    const theme = useTheme()
    const tagManager = useTagManager()
    const monetate = useMonetate()
    const {baseStyle} = theme.components.ListMenu
    const closePopoverRef = useRef(false)
    const hoverTimerRef = useRef(null)

    const cancelPendingOpen = () => {
        if (hoverTimerRef.current) {
            window.clearTimeout(hoverTimerRef.current)
            hoverTimerRef.current = null
        }
    }

    const openWithHoverIntent = () => {
        if (closePopoverRef.current || hoverTimerRef.current) {
            return
        }

        hoverTimerRef.current = window.setTimeout(() => {
            hoverTimerRef.current = null
            if (!closePopoverRef.current) {
                onOpen()
            }
        }, MENU_HOVER_INTENT_DELAY_MS)
    }

    useEffect(() => {
        return () => {
            if (hoverTimerRef.current) {
                window.clearTimeout(hoverTimerRef.current)
            }
        }
    }, [])
    const keyMap = {
        Escape: () => {
            onClose()
            closePopoverRef.current = false
        },
        Enter: () => {
            if (!closePopoverRef.current) {
                onOpen()
            }
        }
    }
    const redirectionProps =
        item?.c_alternativeUrl && item?.c_alternativeUrl?.indexOf('.html') !== -1
            ? {href: item?.c_alternativeUrl}
            : {as: RouteLink, isExternal: false, to: categoryUrlBuilder(item)}
    const spinnerRef = useRef(null)
    const handleNavigation = useNavigationHandler({spinnerRef})
    const navProps = getNavigationProps(redirectionProps.href || redirectionProps.to)

    const handleInternalNavigation = (event) => {
        cancelPendingOpen()
        closePopoverRef.current = true
        handleNavigation(event, navProps, () => {
            // The destination page reads these values while building its Monetate event.
            monetate.setCategoriesViewed(name)
            monetate.setNavigationClicks(name)

            const trackGtmNavigation = () =>
                tagManager.navigationMain('header', name.toLowerCase(), undefined, undefined)

            // Internal navigation keeps this document alive, so only independent GTM
            // work can move out of the pointer task. External links track before unload.
            if (navProps.shouldDeferNavigation) {
                scheduleIdleAnalytics(trackGtmNavigation, 0)
            } else {
                trackGtmNavigation()
            }
        })
        scheduleAfterPaint(() => {
            startTransition(() => {
                onClose()
            })
        })
    }

    return (
        <Box
            position="relative"
            onMouseEnter={openWithHoverIntent}
            onMouseLeave={() => {
                cancelPendingOpen()
                closePopoverRef.current = false
            }}
            {...baseStyle.listMenuTriggerContainer}
            height="60px"
            zIndex={1}
            className={`category-trigger-wrapper category-trigger-wrapper-${item?.id}`}
        >
            <Box ref={spinnerRef} className="hide-element">
                <LoadingSpinner position="fixed" top="auto" background="transparent" />
            </Box>

            {/*** Desktop Category - L1 (with child categories) ***/}
            <ChakraLink
                {...redirectionProps}
                {...{name: name + ' __'}}
                {...(isOpen ? baseStyle.listMenuTriggerLinkActive : {})}
                onKeyDown={(e) => {
                    keyMap[e.key]?.(e)
                }}
                onClick={handleInternalNavigation}
                {...baseStyle.listMenuTriggerLinkIcon}
                {...baseStyle.listMenuTriggerLink}
                className={`category-trigger-link category-trigger-link-${item?.id}`}
                id={`cat-${item?.id}`}
            >
                {name}
            </ChakraLink>

            <Button
                onClick={onOpen}
                onKeyDown={(e) => {
                    keyMap[e.key]?.(e)
                }}
                {...baseStyle.listMenuTriggerIcon}
                _focus={{
                    opacity: '0 !important'
                }}
                className="chevron-down-icon-box"
            >
                <ChevronDownIcon />
            </Button>
        </Box>
    )
}

ListMenuTrigger.propTypes = {
    item: PropTypes.object,
    name: PropTypes.string,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func
}

export {ListMenuTrigger}
